<template>
    <CompassDialog
        class="company-create-dialog"
        :visible.sync="isVisible"
        :title="dialogTitle"
        size="small"
        :closeOnClickOverlay="false"
        @closed="close"
    >
        <template v-if="!requestSent && !companyCreateError">
            <div class="company-create-dialog__form">
                <CompassFormItem
                    :label="form.labels.companyName"
                    required
                    :requiredLabel="$t.__('common.labels.required')"
                >
                    <CompassInput
                        v-model="form.values.companyName"
                        :disabled="isLoadingCustomFields"
                    />
                </CompassFormItem>

                <CompassFormItem
                    v-if="isFrench"
                    :label="form.labels.companyRegistration"
                    :required="isFrench"
                    :requiredLabel="$t.__('common.labels.required')"
                >
                    <CompassInput
                        v-model="form.values.companyRegistration"
                        :disabled="isLoadingCustomFields"
                    />
                </CompassFormItem>

                <template v-if="hasAddress && !isLoadingCountries">
                    <CompassFormItem
                        :label="form.labels.address.label"
                        required
                        :requiredLabel="$t.__('common.labels.required')"
                        :error="form.labels.address.labelError"
                    >
                        <CompassInput
                            v-model="form.values.address.label"
                            :error="form.labels.address.labelError !== ''"
                        />
                    </CompassFormItem>

                    <CompassFormItem
                        :label="form.labels.address.line1"
                        required
                        :requiredLabel="$t.__('common.labels.required')"
                        :error="form.labels.address.line1Error"
                    >
                        <CompassInput
                            v-model="form.values.address.line1"
                            :error="form.labels.address.line1Error !== ''"
                        />
                    </CompassFormItem>

                    <CompassFormItem :label="form.labels.address.line2">
                        <CompassInput v-model="form.values.address.line2" />
                    </CompassFormItem>

                    <div class="company-create-dialog__form-column">
                        <CompassFormItem
                            class="postal-code"
                            :label="form.labels.address.postalCode"
                            required
                            :requiredLabel="$t.__('common.labels.required')"
                            :error="form.labels.address.postalCodeError"
                        >
                            <CompassInput
                                v-model="form.values.address.postalCode"
                                :error="form.labels.address.postalCodeError !== ''"
                            />
                        </CompassFormItem>

                        <CompassFormItem
                            class="city"
                            :label="form.labels.address.city"
                            required
                            :requiredLabel="$t.__('common.labels.required')"
                            :error="form.labels.address.cityError"
                        >
                            <CompassInput
                                v-model="form.values.address.city"
                                :error="form.labels.address.cityError !== ''"
                            />
                        </CompassFormItem>
                    </div>

                    <CompassFormItem
                        :label="form.labels.address.country"
                        required
                        :requiredLabel="$t.__('common.labels.required')"
                    >
                        <CompassSelect
                            v-model="form.values.address.country"
                            menuClass="company-create-select"
                        >
                            <CompassDropdownSelectOption
                                v-for="country in form.countries"
                                :key="country.code"
                                :label="country.name"
                                :value="country.code"
                            />
                        </CompassSelect>
                    </CompassFormItem>
                </template>

                <template v-if="hasAddress && isLoadingCountries">
                    <CompassSkeleton class="skeleton__label" />
                    <CompassSkeleton size="large" class="skeleton__input skeleton__input--spacer" />
                    <CompassSkeleton class="skeleton__label" />
                    <CompassSkeleton size="large" class="skeleton__input" />
                </template>

                <CompassFormItem :label="form.labels.companyType">
                    <CompassSelect
                        v-model="form.values.companyType"
                        :disabled="isLoadingCustomFields"
                        menuClass="company-create-select"
                    >
                        <CompassDropdownSelectOption
                            v-for="item in companyTypes"
                            :key="item.value"
                            :label="item.label"
                            :value="item.value"
                            type="dot"
                            :dotColor="item.dotColor"
                        />
                    </CompassSelect>
                </CompassFormItem>
            </div>
            <template v-if="isLoadingCustomFields">
                <CompassSkeleton class="skeleton__title" />
                <CompassSkeleton class="skeleton__label" />
                <CompassSkeleton size="large" class="skeleton__input skeleton__input--spacer" />
                <CompassSkeleton class="skeleton__label" />
                <CompassSkeleton size="large" class="skeleton__input" />
            </template>
            <template v-if="!!requiredCustomFieldsByType.length">
                <h3 class="typographies-heading-h3 company-create-dialog__title">
                    {{ $l10n.__('Champs personnalisés obligatoires') }}
                </h3>
                <div class="company-create-dialog__form">
                    <CompassFormItem
                        v-for="field in requiredCustomFieldsByType"
                        :key="field.id"
                        :class="field.error ? 'is-error' : ''"
                        :label="field.name"
                        :info="field.description"
                        :error="field.error || ''"
                        required
                        :requiredLabel="$t.__('common.labels.required')"
                    >
                        <component
                            :is="'cf-' + field.type"
                            :id="field.id"
                            :parameters="field.parameters"
                            :value="field.value"
                            :type="field.type"
                            @input="updateValue"
                        />
                    </CompassFormItem>
                </div>
            </template>
        </template>
        <CompassAlert
            v-if="companyCreateError"
            class="company-create-dialog__alert"
            type="error"
            :description="alertText"
            showIcon
            :showClose="false"
        />
        <template v-if="requestSent && !companyCreateError">
            <CompassLoader class="company-create-dialog__loader" />
        </template>
        <template v-if="!requestSent" slot="footer">
            <div class="company-create-dialog__footer">
                <CompassButton
                    :label="$t.__('common.actions.cancel')"
                    type="secondary"
                    @click="handleCancel"
                />
                <CompassButton
                    :label="$t.__('common.actions.save')"
                    :disabled="isLoadingCustomFields"
                    @click="handleSave"
                />
            </div>
        </template>
    </CompassDialog>
</template>

<script>
import { buildCustomFields } from 'utils/customfields';
import CompanyApi from 'api/companies';
import { formatCustomFieldsBeforeSubmit } from '../../helpers/searchbar/searchbarHelper';

export default {
    components: {
        'cf-simple-text': () => import('vcomponents/inputs/customfields/SimpleText.vue'),
        'cf-amount': () => import('vcomponents/inputs/customfields/Amount.vue'),
        'cf-boolean': () => import('vcomponents/inputs/customfields/Boolean.vue'),
        'cf-checkbox': () => import('vcomponents/inputs/customfields/Checkbox.vue'),
        'cf-date': () => import('vcomponents/inputs/customfields/Date.vue'),
        'cf-email': () => import('vcomponents/inputs/customfields/Email.vue'),
        'cf-item': () => import('vcomponents/inputs/customfields/Item.vue'),
        'cf-numeric': () => import('vcomponents/inputs/customfields/Numeric.vue'),
        'cf-contact': () => import('vcomponents/inputs/customfields/Contact.vue'),
        'cf-radio': () => import('vcomponents/inputs/customfields/Radio.vue'),
        'cf-select': () => import('vcomponents/inputs/customfields/Select.vue'),
        'cf-staff': () => import('vcomponents/inputs/customfields/Staff.vue'),
        'cf-company-individual': () => import('vcomponents/inputs/customfields/Third.vue'),
        'cf-time': () => import('vcomponents/inputs/customfields/Time.vue'),
        'cf-rich-text': () => import('vcomponents/inputs/customfields/RichText.vue'),
        'cf-numeric-with-unit': () => import('vcomponents/inputs/customfields/Unit.vue'),
        'cf-url': () => import('vcomponents/inputs/customfields/Url.vue'),
    },
    props: {
        companyToCreate: {
            type: Object,
            required: true,
        },
        countryCode: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            isVisible: true,
            dialogTitle: this.$l10n.__("Ajouter une nouvelle société depuis l'annuaire"),
            dialogSave: this.$l10n.__('Enregistrer'),
            dialogCancel: this.$l10n.__('Annuler'),
            requiredCustomFields: [],
            form: {
                labels: {
                    companyName: this.$l10n.__('Nom de la société'),
                    companyType: this.$l10n.__('Type'),
                    companyRegistration: this.$l10n.__('SIRET'),
                    address: {
                        label: this.$l10n.__("Nom de l'adresse"),
                        labelError: '',
                        line1: this.$l10n.__('Rue'),
                        line1Error: '',
                        line2: this.$l10n.__("Complément d'adresse"),
                        city: this.$l10n.__('Ville'),
                        cityError: '',
                        postalCode: this.$l10n.__('Code postal'),
                        postalCodeError: '',
                        country: this.$l10n.__('Pays'),
                        countryError: '',
                    },
                },
                values: {
                    companyName: this.companyToCreate.name,
                    companyType: 'client',
                    companyRegistration: this.companyToCreate.siret,
                    address: {
                        label: this.$l10n.__('Adresse principale'),
                        line1: this.companyToCreate.address.address_line_1,
                        line2: this.companyToCreate.address.address_line_2 ?? '',
                        city: this.companyToCreate.address.city ?? '',
                        postalCode: this.companyToCreate.address.postal_code ?? '',
                        country: this.countryCode,
                    },
                },
                countries: [
                    {
                        code: 'FR',
                        name: 'France',
                    },
                ],
            },
            companyTypes: [
                { label: 'Client', value: 'client', dotColor: '#2e78d1' },
                { label: 'Prospect', value: 'prospect', dotColor: '#8733a0' },
                { label: 'Fournisseur', value: 'supplier', dotColor: '#c74387' },
            ],
            isLoadingCustomFields: true,
            isLoadingCountries: true,
            requestSent: false,
            companyCreateError: false,
            alertText: this.$l10n.__(
                'Une erreur est survenue lors de la création de société. Veuillez réessayer plus tard ou contacter le support.',
            ),
        };
    },
    computed: {
        isFrench() {
            return this.countryCode === 'FR';
        },
        hasAddress() {
            return Object.values(this.companyToCreate?.address).some((value) => value);
        },
        requiredCustomFieldsByType() {
            return this.requiredCustomFields.filter((field) =>
                field.related_objects.includes(this.form.values.companyType),
            );
        },
    },
    created() {
        ApiClient.post(
            '/custom-fields/search',
            {},
            {
                filters: {
                    is_required: true,
                },
            },
        ).then(({ data: { data } }) => {
            this.requiredCustomFields = buildCustomFields(data);
            this.isLoadingCustomFields = false;
        });

        if (this.hasAddress) {
            this.$ajax.get(
                `/rest/countries`,
                { lang: this.countryCode.toUpperCase() },
                (response) => {
                    this.$nextTick(() => {
                        this.form.countries = Object.values(response.countries)
                            .filter((item) => item.code)
                            .sort((a, b) => b.shortlist - a.shortlist);
                        this.isLoadingCountries = false;
                    });
                },
            );
        } else {
            this.isLoadingCountries = false;
        }
    },
    methods: {
        invalidateForm(field, rule) {
            this.requiredCustomFields = this.requiredCustomFields.map((item) => {
                if (item.id === field.id) {
                    const message =
                        rule.message ||
                        this.$l10n.__('Vous avez oublié de compléter un champs obligatoire');
                    item.error = message;
                    return field;
                }
                return item;
            });
        },
        checkAddressFormRules() {
            let isValid = true;

            for (const [key, value] of Object.entries(this.form.values.address)) {
                if (key !== 'line2' && !value) {
                    isValid = false;
                    this.form.labels.address[`${key}Error`] = this.$l10n.__(
                        'Vous avez oublié de compléter un champs obligatoire',
                    );
                }

                if (key === 'label' && (value.length < 2 || value.length > 200)) {
                    isValid = false;
                    this.form.labels.address[`${key}Error`] = this.$l10n.__(
                        'Le nom de l’adresse doit être entre 2 et 200 caractères',
                    );
                }

                if (
                    (key === 'line1' || key === 'city') &&
                    (value.length < 2 || value.length > 250)
                ) {
                    isValid = false;
                    this.form.labels.address[`${key}Error`] = this.$l10n.__(
                        'Ce champ doit être entre 2 et 200 caractères',
                    );
                }

                if (key === 'postalCode' && (value.length < 2 || value.length > 30)) {
                    isValid = false;
                    this.form.labels.address[`${key}Error`] = this.$l10n.__(
                        'Ce champ doit être entre 2 et 30 caractères',
                    );
                }
            }

            return isValid;
        },
        checkCustomFieldRules() {
            let isValid = true;
            for (const field of this.requiredCustomFieldsByType) {
                for (const rule of field.rules) {
                    if (rule.min && field.value.length < rule.min) {
                        isValid = false;
                        this.invalidateForm(field, rule);
                    }

                    if (rule.max && field.value.length > rule.max) {
                        isValid = false;
                        this.invalidateForm(field, rule);
                    }

                    if (rule.type === 'email') {
                        const emailRegex = new RegExp(
                            /^[A-Za-z0-9_!#$%&'*+/=?`{|}~^.-]+@[A-Za-z0-9.-]+$/,
                            'gm',
                        );
                        if (!emailRegex.test(field.value)) {
                            isValid = false;
                            this.invalidateForm(field, rule);
                        }
                    }

                    if (rule.required || rule.validator) {
                        if (Array.isArray(field.value) && field.value.length === 0) {
                            isValid = false;
                            this.invalidateForm(field, rule);
                        }
                        if (
                            typeof field.value === 'object' &&
                            field.value !== null &&
                            !Object.values(field.value).every((value) => value)
                        ) {
                            isValid = false;
                            this.invalidateForm(field, rule);
                        }
                        if (field.value === null || field.value === undefined) {
                            isValid = false;
                            this.invalidateForm(field, rule);
                        }
                    }
                }
            }
            return isValid;
        },
        handleSave() {
            if (!this.checkCustomFieldRules()) {
                return;
            }

            if (this.hasAddress && !this.checkAddressFormRules()) {
                return;
            }

            this.formattedRequiredCustomFieldsByType = formatCustomFieldsBeforeSubmit(
                this.requiredCustomFieldsByType,
            );

            this.requestSent = true;

            CompanyApi.createCompany({
                name: this.form.values.companyName,
                siret: this.form.values.companyRegistration,
                type: this.form.values.companyType,
                address: this.form.values.address,
                customFields: this.formattedRequiredCustomFieldsByType,
            })
                .then(({ data }) => {
                    if (data.responses.length) {
                        const allResponsesOk = data.responses.every(
                            (response) => response.code === 201 || response.code === 200,
                        );

                        if (!allResponsesOk) {
                            this.companyCreateError = true;

                            if (data.responses[0].code === 201) {
                                CompanyApi.deleteCompany(data.responses[0].response.id);
                            }
                        } else {
                            // company created
                            if (window.pendo?.accountId) {
                                window.pendo.track('searchbar.directory.company.created', {
                                    type: this.form.values.companyType,
                                });
                            }
                            window.location.href = `/companies/${data.responses[0].response.id}`;
                        }
                    }
                })
                .catch((error) => {
                    this.companyCreateError = true;
                    console.error(error);
                });
        },
        handleCancel() {
            this.$emit('close');
        },
        close() {
            this.$emit('close');
        },
        updateValue(updatedField) {
            this.requiredCustomFields = this.requiredCustomFields.map((field) => {
                if (field.id === updatedField.id) {
                    return {
                        ...field,
                        value: updatedField.value,
                    };
                }
                return field;
            });
        },
    },
};
</script>

<style scoped lang="scss">
.skeleton {
    &__title {
        margin: var(--spacing-4) 0 var(--spacing-1);
        width: 70%;
    }

    &__label {
        margin: 0 0 var(--spacing-1);
        width: 110px;
    }

    &__input {
        &::v-deep {
            .compass-skeleton__text {
                height: 32px;
            }
        }

        &--spacer {
            margin: 0 0 var(--spacing-5);
        }
    }
}
.company-create-dialog {
    &__form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-4);

        &-column {
            display: flex;
            gap: var(--spacing-3);

            .postal-code {
                flex: 0 0 35%;
                min-width: 0;
            }

            .city {
                flex: 1 1 auto;
            }
        }
    }

    &__title {
        color: var(--colors-common-text-standard-default, #111);
        margin: var(--spacing-4) 0 var(--spacing-1);
    }

    &__footer {
        display: flex;
        justify-content: flex-end;
        gap: var(--spacing-3, 16px);
        padding-top: var(--spacing-4, 24px);
        padding-right: var(--spacing-5, 32px);
        padding-bottom: var(--spacing-5, 32px);
    }

    &__alert {
        margin-bottom: 48px;
    }

    &__loader {
        color: var(--colors-common-text-standard-default);
        display: flex;
        padding: 64px 0 88px;

        &::v-deep {
            .compass-icon,
            .compass-loader__circle {
                height: 64px;
                width: 64px;
            }
        }
    }

    &::v-deep {
        .is-error .el-input__inner,
        .is-error .el-input__inner:focus,
        .is-error .el-textarea__inner,
        .is-error .el-textarea__inner:focus {
            border-color: var(--colors-common-border-alert);
        }
    }
}
</style>
<style>
#compass-dropdown-container {
    .company-create-select {
        z-index: 1001;
    }
}
</style>
